import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Input, InputNumber, Button, Modal, Select, Spin, Table, Checkbox, message } from 'antd';
import { useReceivableDetailForm } from './hooks/useReceivableDetailForm';
import { receivableDetailApi } from '../../services/receivableDetailApi';
import type { DetailCreate, FG03 } from '../../services/receivableDetailApi';
import { auditLogApi } from '../../services/auditLogApi';
import LookupField from '../../components/LookupField';
import { useModulePermission } from '../../hooks/usePermission';
import {
  AcceptanceDataPanel,
  AcceptanceFieldHint,
  AcceptanceToolbar,
  pwg103f1AcceptanceManifest,
  useAcceptanceMode,
} from '../../features/acceptance';

const { Option } = Select;

const G103Form: React.FC = () => {
  const permissions = useModulePermission('pwg103f1');
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    companyCode, setCompanyCode,
    shipNo, setShipNo,
    receivableAmt, setReceivableAmt,
    paymentAmt, setPaymentAmt,
    returnAmt, setReturnAmt,
    discountAmt, setDiscountAmt,
    complaintAmt, setComplaintAmt,
    currency, setCurrency,
    docType, setDocType, docTypeLabel,
    adjustAmt, setAdjustAmt,
    canClaim, setCanClaim,
    details, setDetails,
    outstandingBalance,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  } = useReceivableDetailForm(permissions.canView);

  const [searchCompany, setSearchCompany] = useState('');
  const [searchShipNo, setSearchShipNo] = useState('');
  const [acceptanceLatest, setAcceptanceLatest] = useState<FG03[]>([]);
  const [acceptanceAudit, setAcceptanceAudit] = useState<unknown[]>([]);
  const [acceptanceDatabaseRecord, setAcceptanceDatabaseRecord] = useState<FG03 | null>(null);
  const [acceptanceSourceRecord, setAcceptanceSourceRecord] = useState<FG03 | null>(null);
  const [acceptanceErrors, setAcceptanceErrors] = useState<string[]>([]);
  const [acceptanceLoading, setAcceptanceLoading] = useState(false);
  const acceptanceMode = useAcceptanceMode();
  const acceptanceEnabled = acceptanceMode.isEnabled(pwg103f1AcceptanceManifest.formId);

  const documentKey = current ? `${current.fg03_01}/${current.fg03_02}` : undefined;

  const loadAcceptanceData = useCallback(async () => {
    if (!acceptanceEnabled) return;
    setAcceptanceLoading(true);
    const [latest, database, audit] = await Promise.allSettled([
      receivableDetailApi.list(1, 3),
      current
        ? receivableDetailApi.get(current.fg03_01, current.fg03_02)
        : Promise.resolve(null),
      auditLogApi.list({
        form_id: pwg103f1AcceptanceManifest.formId,
        doc_key: documentKey,
        page: 1,
        pageSize: 20,
      }),
    ]);
    setAcceptanceLatest(latest.status === 'fulfilled' ? latest.value.data || [] : []);
    setAcceptanceDatabaseRecord(database.status === 'fulfilled' ? database.value : null);
    setAcceptanceAudit(audit.status === 'fulfilled' ? audit.value.data || [] : []);
    setAcceptanceErrors([
      ...(latest.status === 'rejected' ? ['最新三筆 API 讀取失敗。'] : []),
      ...(database.status === 'rejected' ? ['單筆資料庫回讀 API 讀取失敗。'] : []),
      ...(audit.status === 'rejected' ? ['SYS_AuditLog 讀取失敗，不能把本次驗收判定為通過。'] : []),
    ]);
    setAcceptanceLoading(false);
  }, [acceptanceEnabled, current, documentKey]);

  useEffect(() => {
    void loadAcceptanceData();
  }, [loadAcceptanceData]);

  const handleAcceptanceAutoFill = useCallback(() => {
    if (!current) {
      message.warning('請先載入一筆既有應收帳款，驗收模式會複製它成為未送出的草稿。');
      return;
    }
    const sample = current;
    setAcceptanceSourceRecord(sample);
    startAdd();
    setCompanyCode(sample.fg03_01);
    setShipNo('');
    setReceivableAmt(sample.fg03_11);
    setPaymentAmt(sample.fg03_12);
    setReturnAmt(sample.fg03_13);
    setDiscountAmt(sample.fg03_14);
    setComplaintAmt(sample.fg03_15);
    setCurrency(sample.fg03_16);
    setDocType(sample.fg03_21);
    setAdjustAmt(sample.fg03_41);
    setCanClaim(sample.fg03_42);
    setDetails((sample.details || []).map(row => ({
      fg031_03: row.fg031_03,
      fg031_11: row.fg031_11,
      fg031_12: row.fg031_12,
    })));
    message.success('已建立未送出的驗收草稿；請輸入新的交運單號後，再用正式儲存按鈕送出。');
  }, [
    current, setAdjustAmt, setCanClaim, setCompanyCode, setComplaintAmt, setCurrency, setDetails,
    setDiscountAmt, setDocType, setPaymentAmt, setReceivableAmt, setReturnAmt, setShipNo, startAdd,
  ]);

  const H = (fieldId: string) => (
    <AcceptanceFieldHint
      manifest={pwg103f1AcceptanceManifest}
      fieldId={fieldId}
      documentKey={documentKey}
    />
  );

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除「${current.fg03_01} / ${current.fg03_02}」？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleQuickSearch = () => {
    quickSearch(searchCompany.trim(), searchShipNo.trim());
  };

  // Detail table helpers
  const addDetailRow = () => {
    setDetails([...details, { fg031_03: '', fg031_11: 0, fg031_12: null }]);
  };
  const removeDetailRow = (idx: number) => {
    setDetails(details.filter((_, i) => i !== idx));
  };
  const updateDetail = (idx: number, field: keyof DetailCreate, value: any) => {
    const updated = [...details];
    (updated[idx] as any)[field] = value;
    setDetails(updated);
  };

  const detailColumns = [
    {
      title: <>繳款單號 {H('fg031_03')}</>, dataIndex: 'fg031_03', key: 'fg031_03', width: 160,
      render: (v: string, _: DetailCreate, idx: number) =>
        isEditing ? <Input value={v} onChange={e => updateDetail(idx, 'fg031_03', e.target.value)} maxLength={20} /> : v,
    },
    {
      title: <>繳款金額 {H('fg031_11')}</>, dataIndex: 'fg031_11', key: 'fg031_11', width: 140,
      render: (v: number, _: DetailCreate, idx: number) =>
        isEditing ? <InputNumber value={v} onChange={val => updateDetail(idx, 'fg031_11', val ?? 0)} style={{ width: '100%' }} /> : v?.toLocaleString(),
    },
    {
      title: <>繳款日期 {H('fg031_12')}</>, dataIndex: 'fg031_12', key: 'fg031_12', width: 140,
      render: (v: string | null) => v ? v.substring(0, 10) : '-',
    },
    ...(isEditing ? [{
      title: '操作', key: 'action', width: 80,
      render: (_: any, __: DetailCreate, idx: number) =>
        <Button size="small" danger onClick={() => removeDetailRow(idx)}>刪除</Button>,
    }] : []),
  ];

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  return (
    <Spin spinning={loading}>
      <div className="max-w-4xl mx-auto p-6" data-acceptance-action="PWG103-ACT-LIST">
        <h2 className="text-xl font-bold mb-4">
          應收帳款明細資料 (pwg103f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        <AcceptanceToolbar
          manifest={pwg103f1AcceptanceManifest}
          onAutoFill={handleAcceptanceAutoFill}
          documentKey={documentKey}
          autoFillDisabled={!current || isEditing}
          autoFillReason={!current
            ? '請先載入一筆可作為範例的既有應收資料。'
            : isEditing
              ? '請先完成或取消目前編輯。'
              : '複製目前資料為未送出的新草稿；交運單號必須由使用者改成新的唯一值。'}
        />

        {/* Quick Search */}
        <div className="flex items-center gap-2 mb-4">
          <Input placeholder="公司代號" value={searchCompany}
            onChange={e => setSearchCompany(e.target.value)}
            onPressEnter={handleQuickSearch} disabled={isEditing || !permissions.canView}
            style={{ width: 160 }} maxLength={12} />
          <Input placeholder="交運單號" value={searchShipNo}
            onChange={e => setSearchShipNo(e.target.value)}
            onPressEnter={handleQuickSearch} disabled={isEditing || !permissions.canView}
            style={{ width: 120 }} maxLength={7} />
          <Button onClick={handleQuickSearch} disabled={isEditing || !permissions.canView}>查詢</Button>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" data-acceptance-action="PWG103-ACT-GET" onClick={navFirst} disabled={!permissions.canView || isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={!permissions.canView || isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={!permissions.canView || isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={!permissions.canView || isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Master Form */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">公司代號 (FG03_01) {H('fg03_01')}</label>
            <LookupField lookupType="customer" value={companyCode}
              onChange={v => setCompanyCode(v)} onCodeConfirm={v => setCompanyCode(v)}
              maxLength={12} disabled={!isEditing || mode === 'edit'} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">交運單號 (FG03_02) {H('fg03_02')}</label>
            <Input value={shipNo} onChange={e => setShipNo(e.target.value)}
              maxLength={7} disabled={!isEditing || mode === 'edit'} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">幣別代號 (FG03_16) {H('fg03_16')}</label>
            <LookupField lookupType="currency" value={currency}
              onChange={v => setCurrency(v)} onCodeConfirm={v => setCurrency(v)}
              maxLength={3} disabled={!isEditing} style={{ width: 120 }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              單據類別 (FG03_21) {H('fg03_21')} {docTypeLabel && <span className="text-blue-500">— {docTypeLabel}</span>}
            </label>
            <Select value={docType} onChange={setDocType} disabled={!isEditing} style={{ width: 160 }}>
              <Option value="G">大貨</Option>
              <Option value="S">樣品</Option>
              <Option value="I">庫存</Option>
              <Option value="E">調整單</Option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">應收金額 (FG03_11) {H('fg03_11')}</label>
            <InputNumber value={receivableAmt} onChange={v => setReceivableAmt(v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">繳款金額 (FG03_12) {H('fg03_12')}</label>
            <InputNumber value={paymentAmt} onChange={v => setPaymentAmt(v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">退回金額 (FG03_13) {H('fg03_13')}</label>
            <InputNumber value={returnAmt} onChange={v => setReturnAmt(v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">折讓金額 (FG03_14) {H('fg03_14')}</label>
            <InputNumber value={discountAmt} onChange={v => setDiscountAmt(v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">客訴金額 (FG03_15) {H('fg03_15')}</label>
            <InputNumber value={complaintAmt} onChange={v => setComplaintAmt(v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">調整金額 (FG03_41) {H('fg03_41')}</label>
            <InputNumber value={adjustAmt} onChange={v => setAdjustAmt(v ?? 0)}
              disabled={!isEditing} style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">可否請款 (FG03_42) {H('fg03_42')}</label>
            <Checkbox checked={canClaim} disabled>
              {canClaim ? '可請款' : '不可請款'}
            </Checkbox>
          </div>
          <div>
            <label data-contract-field="ui_outstanding_balance" className="block text-sm font-medium text-gray-700 mb-1">應收餘額</label>
            <InputNumber value={outstandingBalance} disabled style={{ width: '100%' }} />
          </div>
        </div>

        {acceptanceEnabled && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={<span className="field-label" data-contract-field="fg03_17">Contract 技術欄位 FG03_17 {H('fg03_17')}</span>}
            description="原 FoxPro 表單沒有這個輸入欄位；驗收模式只揭露目前後端仍傳輸此欄位，不替它猜業務名稱或允許值。"
          />
        )}

        {/* Detail Table (FG031) */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-semibold">繳款明細 (FG031)</h3>
            {isEditing && <Button size="small" onClick={addDetailRow}>新增明細行</Button>}
          </div>
          <Table
            dataSource={details}
            columns={detailColumns}
            rowKey={(_, idx) => String(idx)}
            size="small"
            pagination={false}
            bordered
          />
        </div>

        {/* Audit Fields */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6 space-y-1">
            <div>建立人員: {current.fg03_91} | 日期: {current.fg03_92 || '-'}</div>
            <div>修改人員: {current.fg03_93}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" data-acceptance-action="PWG103-ACT-CREATE" onClick={startAdd} disabled={!permissions.canCreate}>新增</Button>
              <Button data-acceptance-action="PWG103-ACT-UPDATE" onClick={startEdit} disabled={!permissions.canEdit || !current}>修改</Button>
              <Button danger data-acceptance-action="PWG103-ACT-DELETE" onClick={handleDelete} disabled={!permissions.canDelete || !current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save} disabled={mode === 'add' ? !permissions.canCreate : !permissions.canEdit}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>

        <AcceptanceDataPanel
          manifest={pwg103f1AcceptanceManifest}
          documentKey={documentKey}
          snapshots={{
            screenDraft: {
              master: {
                fg03_01: companyCode,
                fg03_02: shipNo,
                fg03_11: receivableAmt,
                fg03_12: paymentAmt,
                fg03_13: returnAmt,
                fg03_14: discountAmt,
                fg03_15: complaintAmt,
                fg03_16: currency,
                fg03_21: docType,
                fg03_41: adjustAmt,
                fg03_42: canClaim,
              },
              details,
            },
            lastApiResponse: acceptanceSourceRecord || current,
            databaseRecord: acceptanceDatabaseRecord,
          }}
          latestRecords={acceptanceLatest}
          history={[]}
          auditEntries={acceptanceAudit}
          loading={acceptanceLoading || loading}
          onRefresh={() => { void loadAcceptanceData(); }}
          observationErrors={acceptanceErrors}
        />
      </div>
    </Spin>
  );
};

export default G103Form;
